/** 클래스 사전 설정 AI 자료 입력 — 사진 축소·요청·결과 타입 (서버: api/_lib/aiPresetExtract.cjs) */
import { supabase } from '../supabaseClient';
import { aiAccessErrorMessage, syncAiQuotaFromResponse } from '../services/aiQuotaService';

export type AiPresetKind = 'calendar' | 'schedule' | 'tracks' | 'crossTopics' | 'fixedPrograms';
export const AI_PRESET_MAX_IMAGES = 4;
export const AI_PRESET_MAX_EXTRA = 500;
/** Claude 비전 권장 긴 변(토큰 절약) */
const MAX_SIDE = 1568;

export type AiCalendarProgram = {
  date: string;
  name: string;
  periods: number[];
  start_time: string;
  end_time: string;
  timetable_name: string;
  /** 편성 시간 수(문서에 있으면), 없으면 0 */
  hours?: number;
};
export type AiCalendarResult = {
  academic_year: number;
  year_starts_on: string;
  year_ends_on: string;
  summer_break_starts_on: string;
  summer_break_ends_on: string;
  winter_break_starts_on: string;
  winter_break_ends_on: string;
  days: Array<{ day: string; kind: 'holiday' | 'closure'; note: string }>;
  programs: AiCalendarProgram[];
  notes: string;
};
export type AiScheduleResult = {
  periods: Array<{ period: number; start_time: string; end_time: string }>;
  after_school_count: number;
  weekday_periods: { mon: number; tue: number; wed: number; thu: number; fri: number };
  notes: string;
};
export type AiTrackRow = {
  name: string;
  short_name: string;
  criteria_hours: number;
  term_scope: 'split' | 'unified';
  category: 'regular' | 'other';
  publisher: string;
};
export type AiTracksResult = { tracks: AiTrackRow[]; notes: string };
export type AiCrossTopicRow = { topic: string; term1_hours: number; term2_hours: number };
export type AiCrossTopicsResult = { topics: AiCrossTopicRow[]; notes: string };
export type AiFixedProgramRow = {
  date: string;
  name: string;
  periods: number[];
  after_school: number[];
  hours: number;
  start_time: string;
  end_time: string;
};
export type AiFixedProgramsResult = { programs: AiFixedProgramRow[]; notes: string };
export type AiPresetResultMap = {
  calendar: AiCalendarResult;
  schedule: AiScheduleResult;
  tracks: AiTracksResult;
  crossTopics: AiCrossTopicsResult;
  fixedPrograms: AiFixedProgramsResult;
};

export type AiPresetImage = { mediaType: 'image/jpeg'; data: string; previewUrl: string; bytes: number };

const ERRORS: Record<string, string> = {
  AI_NOT_CONFIGURED: 'AI 키가 설정되지 않았습니다.',
  UNAUTHORIZED: '로그인이 필요합니다. 다시 로그인해 주세요.',
  FORBIDDEN: '현재 이용권에서는 AI 자료 입력을 사용할 수 없습니다.',
  RATE_LIMITED: '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.',
  NO_IMAGES: '사진을 1장 이상 넣어 주세요.',
  TOO_MANY_IMAGES: `사진은 최대 ${AI_PRESET_MAX_IMAGES}장까지 넣을 수 있어요.`,
  IMAGE_TOO_LARGE: '사진 용량이 너무 큽니다. 장수를 줄이거나 다시 찍어 주세요.',
  INVALID_IMAGE: '지원하지 않는 사진 형식입니다.',
  EXTRA_TOO_LONG: `추가 요청은 ${AI_PRESET_MAX_EXTRA}자까지 쓸 수 있어요.`,
};

function apiUrl(): string {
  const base = (process.env.REACT_APP_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';
  return `${base}/api/ai-preset-extract`;
}

/** 긴 변 1568px JPEG(q 0.82)로 축소 */
export async function resizeImageFile(file: File): Promise<AiPresetImage> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('사진을 읽지 못했습니다.'));
      el.src = url;
    });
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('사진을 처리하지 못했습니다.');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
    const data = dataUrl.slice(dataUrl.indexOf(',') + 1);
    return { mediaType: 'image/jpeg', data, previewUrl: dataUrl, bytes: Math.round((data.length * 3) / 4) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function requestAiPresetExtract<K extends AiPresetKind>(
  params: { kind: K; grade: number; schoolYear: number; extra: string; images: AiPresetImage[] },
  signal?: AbortSignal
): Promise<AiPresetResultMap[K]> {
  /** 스크린샷·개발용: window.__CP_AI_PRESET_MOCK__[kind] 가 있으면 그대로 사용 */
  const mock = (window as unknown as { __CP_AI_PRESET_MOCK__?: Partial<AiPresetResultMap> }).__CP_AI_PRESET_MOCK__;
  if (process.env.NODE_ENV !== 'production' && mock && mock[params.kind]) return mock[params.kind] as AiPresetResultMap[K];
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error(ERRORS.UNAUTHORIZED);
  let res: Response;
  try {
    res = await fetch(apiUrl(), {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        kind: params.kind,
        grade: params.grade,
        schoolYear: params.schoolYear,
        extra: params.extra.slice(0, AI_PRESET_MAX_EXTRA),
        images: params.images.slice(0, AI_PRESET_MAX_IMAGES).map((im) => ({ mediaType: im.mediaType, data: im.data })),
      }),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new Error('AI 서버에 연결하지 못했습니다.');
  }
  const json = (await res.json().catch(() => null)) as { ok?: boolean; data?: unknown; error?: string } | null;
  syncAiQuotaFromResponse(json);
  if (!json || !json.ok || !json.data) {
    const code = json?.error || `HTTP_${res.status}`;
    throw new Error(aiAccessErrorMessage(code, json) ?? ERRORS[code] ?? `AI 결과를 받지 못했습니다. (${code})`);
  }
  return json.data as AiPresetResultMap[K];
}

/** 3월 시작 학년도 */
export function guessSchoolYear(d = new Date()): number {
  return d.getMonth() < 2 ? d.getFullYear() - 1 : d.getFullYear();
}

/** 클래스 이름 `제목::1,2` → 학년 번호들 */
export function gradeNumbersFromClassName(raw: string): number[] {
  const rest = String(raw ?? '').split('::')[1];
  if (!rest) return [];
  return rest
    .split(',')
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 6);
}
